import React from "react";
import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { RootState } from "src/store/store";
import { Admin_Labels_Eng, STATUS_LABELS_ENG } from "src/common/constants/englishLabels";
import { Admin_Labels_Hindi, STATUS_LABELS_HIN } from "src/common/constants/hindiLabels";

type ChartsProps = {
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
};

export const Charts: React.FC<ChartsProps> = ({ approvedCount, pendingCount, rejectedCount }) => {
  const [hovered, setHovered] = React.useState<null | "APPROVED" | "PENDING" | "REJECTED">(null);
  const total = Math.max(0, (approvedCount || 0) + (pendingCount || 0) + (rejectedCount || 0));
  const approvedPct = total ? Math.round((approvedCount / total) * 100) : 0;
  const pendingPct = total ? Math.round((pendingCount / total) * 100) : 0;
  const rejectedPct = total ? Math.round((rejectedCount / total) * 100) : 0;

  // Helper to decide if inline label should be shown for very narrow segments
  //const showInlineLabel = (pct: number) => pct >= 8; // hide labels for very small widths to prevent overlap

  const globalInfo = useSelector((state: RootState) => state.globalInfo);
    const { globalLanguage } = globalInfo;
    const isHindiLabels = globalLanguage === 'HI';
  
  return (
    <Flex direction={{ base: "column" }} gap={6}>
      <Box bg="#FFFFFF" border="1px solid #EDEDED" borderRadius="16px" p={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontWeight="600" color="#4A4E69" letterSpacing="0.5px">{isHindiLabels? Admin_Labels_Hindi.Approval_Status_Distribution: Admin_Labels_Eng.Approval_Status_Distribution}
        </Text>
        </Flex>
        <>
          <Box position="relative" height="12px" bg="#F5F5F5" borderRadius="999px" overflow="hidden">
            <Flex width="100%" height="100%">
              {/* Approved Segment */}
              <Tooltip label={`Approved: ${approvedPct}% (${approvedCount})`} hasArrow openDelay={150}>
                <Box
                  width={`${approvedPct}%`}
                  bg="#22C55E"
                  onMouseEnter={() => setHovered("APPROVED")}
                  onMouseLeave={() => setHovered(null)}
                  position="relative"
                >
                  {(approvedPct > 0 || hovered === "APPROVED") && (
                    <Text
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      fontSize="xs"
                      color="#FFFFFF"
                      fontWeight="600"
                      pointerEvents="none"
                    >
                      {approvedPct}%
                    </Text>
                  )}
                </Box>
              </Tooltip>

              {/* Pending Segment */}
              <Tooltip label={`Pending: ${pendingPct}% (${pendingCount})`} hasArrow openDelay={150}>
                <Box
                  width={`${pendingPct}%`}
                  bg="#F59E0B"
                  onMouseEnter={() => setHovered("PENDING")}
                  onMouseLeave={() => setHovered(null)}
                  position="relative"
                >
                  {(pendingPct > 0 || hovered === "PENDING") && (
                    <Text
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      fontSize="xs"
                      color="#FFFFFF"
                      fontWeight="600"
                      pointerEvents="none"
                    >
                      {pendingPct}%
                    </Text>
                  )}
                </Box>
              </Tooltip>

              {/* Rejected Segment */}
              <Tooltip label={`Rejected: ${rejectedPct}% (${rejectedCount})`} hasArrow openDelay={150}>
                <Box
                  width={`${rejectedPct}%`}
                  bg="#EF4444"
                  onMouseEnter={() => setHovered("REJECTED")}
                  onMouseLeave={() => setHovered(null)}
                  position="relative"
                >
                  {(rejectedPct > 0 || hovered === "REJECTED") && (
                    <Text
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      fontSize="xs"
                      color="#FFFFFF"
                      fontWeight="600"
                      pointerEvents="none"
                    >
                      {rejectedPct}%
                    </Text>
                  )}
                </Box>
              </Tooltip>
            </Flex>

            {/* Fallback center label when segments are too small: show hovered segment */}
            {approvedPct === 0 && pendingPct === 0 && rejectedPct === 0 && (
              <Text position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" fontSize="xs" color="#FFFFFF" fontWeight="600">
                0%
              </Text>
            )}
          </Box>
          <Flex justify="flex-start" mt={3} gap={6} align="center">
            <Flex align="center" gap={2}><Box w="8px" h="8px" borderRadius="full" bg="#22C55E" /><Text fontSize="sm">{isHindiLabels? STATUS_LABELS_HIN.APPROVED: STATUS_LABELS_ENG.APPROVED}</Text></Flex>
            <Flex align="center" gap={2}><Box w="8px" h="8px" borderRadius="full" bg="#F59E0B" /><Text fontSize="sm">{isHindiLabels? STATUS_LABELS_HIN.PENDING: STATUS_LABELS_ENG.PENDING}</Text></Flex>
            <Flex align="center" gap={2}><Box w="8px" h="8px" borderRadius="full" bg="#EF4444" /><Text fontSize="sm">{isHindiLabels? STATUS_LABELS_HIN.REJECTED: STATUS_LABELS_ENG.REJECTED}</Text></Flex>
          </Flex>
        </>
      </Box>
    </Flex>
  );
};

export default Charts;
